import React from 'react';
import PropTypes from 'prop-types';
import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import classNames from 'classnames';
import {defineMessages, FormattedMessage, intlShape} from 'react-intl';

import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants';
import {getStageDimensions} from '../../lib/screen-utils.js';

import styles from './hardware-header.css';

import largeStageIcon from './icon--large-stage.svg';
import smallStageIcon from './icon--small-stage.svg';
import hideStageIcon from './icon--hide-stage.svg';
import uploadIcon from './icon--upload.svg';

const messages = defineMessages({
    uploadMessage: {
        defaultMessage: 'Upload',
        description: 'Button to upload program',
        id: 'gui.hardwareHeader.upload'
    },
    largeStageSizeMessage: {
        defaultMessage: 'Switch to large stage',
        description: 'Button to upload code to device alt',
        id: 'gui.stageHeader.codeStageSizeLarge'
    },
    smallStageSizeMessage: {
        defaultMessage: 'Switch to small stage',
        description: 'Button to change stage size to small',
        id: 'gui.stageHeader.codeStageSizeSmall'
    },
    hideStageSizeMessage: {
        defaultMessage: 'Hide stage',
        description: 'Button to hide stage',
        id: 'gui.stageHeader.hideStage'
    }
});

// PATCH (openblock-web-compiler): tek "Upload" butonu yerine Arduino
// IDE'dekine benzer üç ayrı işlem:
//   1) Derle (Compile)            — sadece derler, karta yazmaz
//   2) Yükle (Upload)             — en son derlenen sonucu, yeniden
//                                    derlemeden doğrudan karta yazar
//   3) Derle + Yükle              — ikisini art arda yapar (eski tek buton)
// Birden fazla kart aynı kodu kullanacaksa: bir kez "Derle", sonra her
// kartta ayrı ayrı "Yükle" ile tekrar derlemeden hızlıca yükleyebilirsiniz.
const HardwareHeaderComponent = props => {
    const {
        deviceId,
        nanoBootloaderVariant,
        onCompile,
        onCompileAndUpload,
        onNanoVariantChange,
        onSetStageLarge,
        onSetStageSmall,
        onSetStageHide,
        onUploadLastCompiled,
        stageSize
    } = props;
    const stageDimensions = getStageDimensions(stageSize === STAGE_DISPLAY_SIZES.hide ?
        STAGE_DISPLAY_SIZES.small : stageSize, null);
    const isNano = deviceId === 'arduinoNano';
    return (
        <Box
            className={classNames(
                styles.hardwareHeaderWrapper
            )}
            style={{width: stageDimensions.width + 2}}
        >
            <div className={styles.uploadGroup}>
                {isNano ? (
                    <select
                        className={styles.nanoBootloaderSelect}
                        title="Nano kartınızın bootloader'ı eskiyse (klon kartlarda yaygın) 'Eski' seçin"
                        value={nanoBootloaderVariant}
                        onChange={e => onNanoVariantChange(e.target.value)}
                    >
                        <option value="new">Yeni Bootloader</option>
                        <option value="old">Eski Bootloader</option>
                    </select>
                ) : null}
                <div
                    className={classNames(styles.uploadButton, styles.uploadButtonCompile)}
                    title="Sadece derle, karta yazma"
                    onClick={onCompile}
                >
                    <FormattedMessage
                        defaultMessage="Compile"
                        description="Button to only compile the program without flashing"
                        id="gui.hardwareHeader.compile"
                    />
                </div>
                <div
                    className={classNames(styles.uploadButton, styles.uploadButtonUploadLast)}
                    title="En son derlenen kodu, yeniden derlemeden karta yaz"
                    onClick={onUploadLastCompiled}
                >
                    <img
                        alt={props.intl.formatMessage(messages.uploadMessage)}
                        className={styles.uploadIcon}
                        draggable={false}
                        src={uploadIcon}
                    />
                    <FormattedMessage
                        defaultMessage="Upload"
                        description="Button to upload the last compiled program without recompiling"
                        id="gui.hardwareHeader.uploadLast"
                    />
                </div>
                <div
                    className={classNames(styles.uploadButton, styles.uploadButtonCompileAndUpload)}
                    title="Derle ve hemen karta yaz"
                    onClick={onCompileAndUpload}
                >
                    <FormattedMessage
                        defaultMessage="Compile + Upload"
                        description="Button to compile then immediately upload the program"
                        id="gui.hardwareHeader.compileAndUpload"
                    />
                </div>
            </div>
            <div className={styles.stageSizeToggleGroup}>
                <div>
                    <Button
                        className={classNames(
                            styles.stageButton,
                            styles.stageButtonFirst,
                            (stageSize === STAGE_DISPLAY_SIZES.small) ? null : styles.stageButtonToggledOff
                        )}
                        onClick={onSetStageSmall}
                    >
                        <img
                            alt={props.intl.formatMessage(messages.smallStageSizeMessage)}
                            className={styles.stageButtonIcon}
                            draggable={false}
                            src={smallStageIcon}
                        />
                    </Button>
                </div>
                <div>
                    <Button
                        className={classNames(
                            styles.stageButton,
                            styles.stageButtonLast,
                            (stageSize === STAGE_DISPLAY_SIZES.large) ? null : styles.stageButtonToggledOff
                        )}
                        onClick={onSetStageLarge}
                    >
                        <img
                            alt={props.intl.formatMessage(messages.largeStageSizeMessage)}
                            className={styles.stageButtonIcon}
                            draggable={false}
                            src={largeStageIcon}
                        />
                    </Button>
                </div>
            </div>
            <div className={styles.stageHideGroup}>
                <div>
                    <Button
                        className={classNames(
                            styles.stageButton,
                            (stageSize === STAGE_DISPLAY_SIZES.hide) ? null : styles.stageButtonToggledOff
                        )}
                        onClick={onSetStageHide}
                    >
                        <img
                            alt={props.intl.formatMessage(messages.hideStageSizeMessage)}
                            className={styles.stageButtonIcon}
                            draggable={false}
                            src={hideStageIcon}
                        />
                    </Button>
                </div>
            </div>
        </Box>
    );
};

HardwareHeaderComponent.propTypes = {
    deviceId: PropTypes.string,
    intl: intlShape,
    nanoBootloaderVariant: PropTypes.oneOf(['new', 'old']),
    onCompile: PropTypes.func.isRequired,
    onCompileAndUpload: PropTypes.func.isRequired,
    onNanoVariantChange: PropTypes.func,
    onSetStageLarge: PropTypes.func.isRequired,
    onSetStageSmall: PropTypes.func.isRequired,
    onSetStageHide: PropTypes.func.isRequired,
    onUploadLastCompiled: PropTypes.func.isRequired,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired
};

export default HardwareHeaderComponent;
